import { Injectable } from "@angular/core";

/**
 * Carries the master password hash computed during login-page biometric setup
 * (fingerprint/Face ID) forward to the two-factor-setup screen, so the user
 * isn't asked to re-enter their password immediately after typing it once.
 * In-memory only, single-use — never persisted to disk or state storage.
 *
 * The hash expires after MAX_AGE_MS so an abandoned setup (user navigates away
 * before the two-factor-setup dialog opens) can't leave a stale hash sitting in
 * memory to be picked up by an unrelated later login/session.
 *
 * Also carries the setup intent itself across the 2FA hop: a login that needs a 2FA
 * challenge navigates to the `2fa` route, destroying LoginComponent (and its
 * pendingBiometricSetup flag) before setup can be routed to. The intent is armed
 * there and consumed by TwoFactorComponent once the challenge is cleared. No hash
 * travels with it — the active key isn't set until 2FA completes, so the setup
 * screen falls back to its own password prompt.
 */
@Injectable({ providedIn: "root" })
export class BiometricSetupHandoffService {
  private static readonly MAX_AGE_MS = 30_000;
  // Matches AuthService's 2FA session timeout — once that lapses the 2FA screen can't
  // complete the login anyway, so an intent armed for it must not outlive it.
  private static readonly PENDING_SETUP_MAX_AGE_MS = 2 * 60 * 1000;

  private pendingHash: string | null = null;
  private setAt = 0;
  private pendingSetupArmedAt: number | null = null;

  setHash(hash: string): void {
    this.pendingHash = hash;
    this.setAt = Date.now();
  }

  /**
   * Invalidates any pending hash. Callers that fail to compute a fresh hash for the
   * current login must call this rather than leaving a stale one in place — otherwise a
   * still-unexpired hash from an earlier, unrelated attempt could get consumed by the
   * current session and produce a biometric credential built from the wrong password.
   */
  clear(): void {
    this.pendingHash = null;
  }

  consumeHash(): string | null {
    const hash = this.pendingHash;
    this.pendingHash = null;
    if (hash == null || Date.now() - this.setAt > BiometricSetupHandoffService.MAX_AGE_MS) {
      return null;
    }
    return hash;
  }

  armPendingSetup(): void {
    this.pendingSetupArmedAt = Date.now();
  }

  clearPendingSetup(): void {
    this.pendingSetupArmedAt = null;
  }

  consumePendingSetup(): boolean {
    const armedAt = this.pendingSetupArmedAt;
    this.pendingSetupArmedAt = null;
    return (
      armedAt != null &&
      Date.now() - armedAt <= BiometricSetupHandoffService.PENDING_SETUP_MAX_AGE_MS
    );
  }
}
